/**
 * Pipeline Labs Company Filters Component
 *
 * Handles company-related filters with camelCase field names for Pipeline Labs API
 * Uses interactive components: TagInput, MultiSelectGrid, AutocompleteInput, Dropdown
 */

import React from 'react';
import TagInput from '../inputs/TagInput';
import MultiSelectGrid from '../inputs/MultiSelectGrid';
import AutocompleteInput from '../inputs/AutocompleteInput';
import {
  EMPLOYEE_SIZE_RANGES,
  COMPANY_NAME_MATCH_MODES,
  COMPANY_DOMAIN_MATCH_MODES
} from '../../../constants/pipelineLabsOptions';
import { PIPELINE_LABS_INDUSTRIES } from '../../../constants/pipelineLabsIndustries';
import { COUNTRIES, REGIONS } from '../../../constants/autocompleteData';

const PipelineLabsCompanyFilters = ({ filters, setFilters }) => {
  const handleArrayChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <>
      {/* Company Names - TagInput for free-text entry */}
      <TagInput
        label="Company Names (Include)"
        value={filters.companyNameIncludes || []}
        onChange={(value) => handleArrayChange('companyNameIncludes', value)}
        placeholder="e.g., Microsoft, Google (Press Enter to add)"
        hint="Add specific company names to target"
      />

      <TagInput
        label="Company Names (Exclude)"
        value={filters.companyNameExcludes || []}
        onChange={(value) => handleArrayChange('companyNameExcludes', value)}
        placeholder="e.g., Competitors (Press Enter to add)"
        hint="Exclude specific companies"
      />

      {/* Company Name Match Mode - Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--color-text-secondary)',
          marginBottom: '8px'
        }}>
          Company Name Match Mode
        </label>
        <select
          value={filters.companyNameMatchMode || 'phrase'}
          onChange={(e) => handleSelectChange('companyNameMatchMode', e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          {COMPANY_NAME_MATCH_MODES.map(mode => (
            <option key={mode.value} value={mode.value}>{mode.label}</option>
          ))}
        </select>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          marginTop: '4px'
        }}>
          Phrase: matches partial names | Exact: requires complete match
        </p>
      </div>

      {/* Company Domains - TagInput */}
      <TagInput
        label="Company Domains (Include)"
        value={filters.companyDomainIncludes || []}
        onChange={(value) => handleArrayChange('companyDomainIncludes', value)}
        placeholder="e.g., microsoft.com (Press Enter to add)"
        hint="Add company domains to target"
      />

      <TagInput
        label="Company Domains (Exclude)"
        value={filters.companyDomainExcludes || []}
        onChange={(value) => handleArrayChange('companyDomainExcludes', value)}
        placeholder="e.g., competitor.com (Press Enter to add)"
        hint="Exclude specific domains"
      />

      {/* Company Domain Match Mode - Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--color-text-secondary)',
          marginBottom: '8px'
        }}>
          Domain Match Mode
        </label>
        <select
          value={filters.companyDomainMatchMode || 'strict'}
          onChange={(e) => handleSelectChange('companyDomainMatchMode', e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          {COMPANY_DOMAIN_MATCH_MODES.map(mode => (
            <option key={mode.value} value={mode.value}>{mode.label}</option>
          ))}
        </select>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          marginTop: '4px'
        }}>
          Strict: exact domain only | Contains: matches subdomains
        </p>
      </div>

      {/* Employee Size - MultiSelectGrid for 12 ranges */}
      <MultiSelectGrid
        label="Employee Size Ranges (Include)"
        options={EMPLOYEE_SIZE_RANGES}
        value={filters.companyEmployeeSizeIncludes || []}
        onChange={(value) => handleArrayChange('companyEmployeeSizeIncludes', value)}
        minWidth="180px"
        hint="Select one or more employee size ranges"
      />

      <MultiSelectGrid
        label="Employee Size Ranges (Exclude)"
        options={EMPLOYEE_SIZE_RANGES}
        value={filters.companyEmployeeSizeExcludes || []}
        onChange={(value) => handleArrayChange('companyEmployeeSizeExcludes', value)}
        minWidth="180px"
        hint="Exclude specific size ranges"
      />

      {/* Company Industries - AutocompleteInput for 148 industries (strict mode - no custom values) */}
      <AutocompleteInput
        label="Industries (Include)"
        value={filters.companyIndustryIncludes || []}
        onChange={(value) => handleArrayChange('companyIndustryIncludes', value)}
        placeholder="Type to search industries..."
        suggestions={PIPELINE_LABS_INDUSTRIES}
        maxTags={50}
        hint="Search and select from 148 LinkedIn-standard industries"
        strictMode={true}
      />

      <AutocompleteInput
        label="Industries (Exclude)"
        value={filters.companyIndustryExcludes || []}
        onChange={(value) => handleArrayChange('companyIndustryExcludes', value)}
        placeholder="Type to search industries..."
        suggestions={PIPELINE_LABS_INDUSTRIES}
        maxTags={30}
        hint="Exclude specific industries"
        strictMode={true}
      />

      {/* Company Keywords - TagInput for free-text */}
      <TagInput
        label="Company Keywords (Include)"
        value={filters.companyKeywordIncludes || []}
        onChange={(value) => handleArrayChange('companyKeywordIncludes', value)}
        placeholder="e.g., SaaS, B2B, Enterprise (Press Enter to add)"
        hint="Searches across company name, description, and specialties"
      />

      <TagInput
        label="Company Keywords (Exclude)"
        value={filters.companyKeywordExcludes || []}
        onChange={(value) => handleArrayChange('companyKeywordExcludes', value)}
        placeholder="e.g., Retail, Consumer (Press Enter to add)"
        hint="Exclude companies with specific keywords"
      />

      {/* Company Location - Countries - AutocompleteInput for 237 countries (strict mode) */}
      <AutocompleteInput
        label="Company Countries (Include)"
        value={filters.companyLocationCountryIncludes || []}
        onChange={(value) => handleArrayChange('companyLocationCountryIncludes', value)}
        placeholder="Type to search countries..."
        suggestions={COUNTRIES}
        hint="Company headquarters - country (237 countries available)"
        strictMode={true}
      />

      <AutocompleteInput
        label="Company Countries (Exclude)"
        value={filters.companyLocationCountryExcludes || []}
        onChange={(value) => handleArrayChange('companyLocationCountryExcludes', value)}
        placeholder="Type to search countries..."
        suggestions={COUNTRIES}
        hint="Exclude specific countries"
        strictMode={true}
      />

      {/* Company Location - States - AutocompleteInput for 494 states/regions (strict mode) */}
      <AutocompleteInput
        label="Company States/Regions (Include)"
        value={filters.companyLocationStateIncludes || []}
        onChange={(value) => handleArrayChange('companyLocationStateIncludes', value)}
        placeholder="Type to search states/regions..."
        suggestions={REGIONS}
        hint="Company headquarters - state/region (494 options available)"
        strictMode={true}
      />

      <AutocompleteInput
        label="Company States/Regions (Exclude)"
        value={filters.companyLocationStateExcludes || []}
        onChange={(value) => handleArrayChange('companyLocationStateExcludes', value)}
        placeholder="Type to search states/regions..."
        suggestions={REGIONS}
        hint="Exclude specific states/regions"
        strictMode={true}
      />

      {/* Company Location - Cities - TagInput for free-text */}
      <TagInput
        label="Company Cities (Include)"
        value={filters.companyLocationCityIncludes || []}
        onChange={(value) => handleArrayChange('companyLocationCityIncludes', value)}
        placeholder="e.g., San Francisco, New York (Press Enter to add)"
        hint="Add specific cities where companies are headquartered"
      />

      <TagInput
        label="Company Cities (Exclude)"
        value={filters.companyLocationCityExcludes || []}
        onChange={(value) => handleArrayChange('companyLocationCityExcludes', value)}
        placeholder="e.g., Remote (Press Enter to add)"
        hint="Exclude specific cities"
      />
    </>
  );
};

export default PipelineLabsCompanyFilters;
